'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Globe, MapPin, X } from 'lucide-react';
import { type LeaderboardEvent } from '@impact/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { Flag } from '@/components/ui/flag/flag';

export function LeaderboardFilters({
  events,
  selectedCountry,
  selectedEvent,
  onCountryChange,
  onEventChange,
  showCountryFilter = true,
  showEventFilter = true,
}: {
  events: LeaderboardEvent[] | undefined;
  selectedCountry: string | undefined;
  selectedEvent: string | undefined;
  onCountryChange: (code: string | undefined) => void;
  onEventChange: (id: string | undefined) => void;
  showCountryFilter?: boolean;
  showEventFilter?: boolean;
}) {
  const t = useTranslations('leaderboard');

  const countries = useMemo(() => {
    if (!events) return [];
    const seen = new Map<string, string>();
    for (const e of events) {
      if (!seen.has(e.countryCode)) {
        seen.set(e.countryCode, e.countryName);
      }
    }
    return Array.from(seen, ([code, name]) => ({ code, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (!events || !selectedCountry) return [];
    return events.filter((e) => e.countryCode === selectedCountry);
  }, [events, selectedCountry]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {showCountryFilter && (
        <Select
          value={selectedCountry ?? '__all__'}
          onValueChange={(v) => onCountryChange(v === '__all__' ? undefined : v)}
        >
          <SelectTrigger className="w-auto h-9 gap-1.5 rounded-lg px-3 bg-white/80 border-stone-200 text-sm text-stone-700 hover:bg-white">
            {!selectedCountry && <Globe className="w-3.5 h-3.5 flex-shrink-0 text-stone-400" />}
            <SelectValue placeholder={t('allCountries')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('allCountries')}</SelectItem>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                <span className="inline-flex items-center gap-2">
                  <Flag code={c.code} size="xs" />
                  {c.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {showEventFilter && selectedCountry && filteredEvents.length > 0 && (
        <Select
          value={selectedEvent ?? '__all__'}
          onValueChange={(v) => onEventChange(v === '__all__' ? undefined : v)}
        >
          <SelectTrigger className="w-auto h-9 gap-1.5 rounded-lg px-3 bg-white/80 border-stone-200 text-sm text-stone-700 hover:bg-white">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-stone-400" />
            <SelectValue placeholder={t('allEvents')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('allEvents')}</SelectItem>
            {filteredEvents.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {(selectedCountry || selectedEvent) && (
        <button
          onClick={() => {
            onCountryChange(undefined);
            onEventChange(undefined);
          }}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
          aria-label={t('resetFilters')}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
